import React, { useState, useEffect } from "react";
import Item from "./Item";
import { createUseStyles } from "react-jss";
import Slider from "react-slick";

const useStyles = createUseStyles({
  container: {
    width: "1400px",
    margin: "0 auto",
    textAlign: "center",
  },
  categoriesContainer: {
    padding: "3rem 0",
    "&:nth-child(even)": {
      backgroundColor: "#F3F7F7",
      color: "#25282D",
    },
    "&:nth-child(odd)": {
      backgroundColor: "#25282D",
      color: "#F3F7F7",
    },
  },
  content: {
    position: "relative",
    width: "1100px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    marginBottom: "5rem",

    "& h2": {
      fontFamily: "LexendDeca-Bold",
      fontWeight: "bold",
      fontSize: "2.6rem",
      lineHeight: "125%",
      marginBottom: "2rem",
    },
    "& p": {
      fontFamily: "LexendDeca-Light",
      fontWeight: "normal",
      fontSize: "2rem",
      lineHeight: "1.5em",
      letterSpacing: "0.03em",
      margin: "0 10rem",
      whiteSpace: "pre-line",
    },
  },
  itemsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    position: "relative",
    margin: "0 -1.5rem",

    "& div": {
      marginRight: "5px",
    },
  },
  ctaButton: {
    margin: "2rem auto",
    textAlign: "center",

    "& a": {
      display: "inline-block",
      border: "2px solid #C93995",
      backgroundColor: "#C93995",
      color: "white",
      height: "4.5rem",
      padding: "0 1.6rem",
      borderRadius: "3px",
      fontSize: "1.6rem",
      letterSpacing: ".05rem",
      lineHeight: "4.5rem",
      textDecoration: "none",
    },
  },
  customSlickArrows: {
    "& .slick-list > div": {
      marginLeft: 0,
    },
    "& .slick-prev": {
      width: "auto",
      height: "auto",
      top: "calc(50% - 20px)",
      left: "-1px",
      zIndex: 1,
      opacity: 1,
      transform: "rotate(180deg)",
      transformOrigin: "top",
    },
    "& .slick-prev:before": {
      content: "''",
    },
    "& .slick-next": {
      width: "auto",
      height: "auto",
      top: "calc(50% - 40px)",
      right: "4px",
      zIndex: 1,
      opacity: 1,
    },
    "& .slick-next:before": {
      content: "''",
    },
  },
  "@media screen and (max-width: 1400px)": {
    container: {
      width: "100%",
      margin: "0 auto",
    },
    content: {
      width: "100%",
      margin: "1rem auto",
    },
    categoriesContainer: {
      padding: "2rem 0",
    },
  },
  "@media screen and (max-width: 768px)": {
    container: {
      width: "100%",
      background: "none !important",
      paddingTop: "0",
    },
    content: {
      width: "100%",
      margin: "1rem auto",
      paddingTop: "1rem",
    },
    itemsContainer: {
      margin: 0,
    },
    title: {
      "& h2": {
        fontSize: "2.5rem",
        marginTop: "3rem",
        marginBottom: "1rem",
        padding: "0 2rem",
      },
      "& p": {
        margin: "0 3rem",
        fontSize: "1.8rem",
      },
    },
  },
});

function filterItemsByCategory(items, categoryId) {
  return items?.item_ids
    ?.map((itemId) => items.data[itemId])
    .filter(
      (item) =>
        (item.category_ids?.findIndex((id) => id === categoryId) ?? -1) !== -1
    );
}

function NextArrow(props) {
  const { className, onClick } = props;
  return (
    <div className={className} style={{ display: "block" }} onClick={onClick}>
      <img
        style={{ width: "100%" }}
        alt="next"
        src="https://folloze.s3.us-west-1.amazonaws.com/icon-carosell%402x.png"
      />
    </div>
  );
}

function PrevArrow(props) {
  const { className, onClick } = props;
  return (
    <div className={className} style={{ display: "block" }} onClick={onClick}>
      <img
        style={{ width: "100%" }}
        alt="prev"
        src="https://folloze.s3.us-west-1.amazonaws.com/icon-carosell%402x.png"
      />
    </div>
  );
}

function Category({ category, allItems }) {
  const categoryItems = filterItemsByCategory(allItems, category.id);
  const classes = useStyles();
  const [slideLimit, setSlideLimit] = useState(4);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    arrows: true,
    className: classes.customSlickArrows,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    variableWidth: false,
    swipeToSlide: false,
    draggable: false,
    responsive: [
      {
        breakpoint: 1100,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          variableWidth: true,
          swipeToSlide: true,
          draggable: true,
        },
      },
    ],
  };

  let resizeWindow = () => {
    setSlideLimit(window.innerWidth < 768 ? 2 : 4);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  return (
    <div className={classes.categoriesContainer}>
      <div className={classes.container}>
        <div className={classes.content}>
          <div id={`category-${category.slug}`} className={classes.title}>
            <h2>{category.name}</h2>
            <p>{category.description}</p>
          </div>

          {/* <pre>{JSON.stringify(category, null, 2)}</pre> */}

          {categoryItems?.length > slideLimit ? (
            <Slider {...settings}>
              {categoryItems?.map((item, index) => (
                <Item
                  key={item.id}
                  item={item}
                  index={index}
                  category={category}
                />
              ))}
            </Slider>
          ) : (
            <div
              className={classes.itemsContainer}
              style={{ textAlign: "center" }}
            >
              {categoryItems?.map((item, index) => (
                <Item
                  key={item.id}
                  item={item}
                  index={index}
                  category={category}
                />
              ))}
            </div>
          )}

          {category.slug === "sunny-side-up" && (
            <div className={classes.ctaButton}>
              <a href={podcastAddress()}>Check Out All of the Episodes Now</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Category;

function podcastAddress() {
  if (window.FollozeState.initialState.board.slug === "a477d1") {
    return "/f530a8/podcast#content";
  }
  return "/resources/podcast#content";
}
